import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { AdminService } from 'src/app/service/admin.service';
import { EventsService } from 'src/app/service/events.service';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { Event } from 'src/app/model/event';
import { NotificationService } from 'src/app/service/notification.service';
import { Submission, SubmissionStatus } from 'src/app/model/paper';
import { FormGroup, FormBuilder } from '@angular/forms';
import { calendarTimezone } from 'src/app/enum/calendar.timezone';
import * as moment from 'moment-timezone';
import { TimezoneService } from 'src/app/service/timezone.service';
import { CountryPipe } from 'src/app/pipe/country.pipe';
import { TranslateService } from '@ngx-translate/core';
import { programSheetCss, programSheetColorsHeader, ProgramSheetComments } from './program-sheet.css';
import { isLightBackground } from 'src/app/lib/color-contrast';
import { DEFAULT_SESSION_COLOR } from 'src/app/lib/session-color';
import { paletteNameOf } from 'src/app/lib/session-palette';
import { EVENT_ADMIN_ICONS } from '../../event-admin-icons';
import { MatDialog } from '@angular/material/dialog';
import { GeneralSessionDialogComponent } from 'src/app/component/general-session-dialog/general-session-dialog.component';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import * as Dayjs from 'dayjs';
import _ from 'lodash';

interface calendarTimeZone {
  id: string;
  value: string;
  offset: number;
}

@Component({
  selector: 'app-event-program',
  templateUrl: './event-program.component.html',
  styleUrls: ['./event-program.component.scss']
})
export class EventProgramComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Ícone da página, o mesmo do item "Listar Programa" no menu lateral. */
  public icons = EVENT_ADMIN_ICONS;

  /** `<style>` da folha, injetado no documento e removido ao sair. */
  private sheetStyleEl: HTMLStyleElement;
  event: Event;

  /** Preferências de exibição (os toggles da barra), lembradas entre visitas. */
  static readonly FLAGS_STORAGE_KEY = 'jems3-program-flags';

  /** Fuso escolhido em visitas anteriores (`event`, `browser` ou um id da lista). */
  private restoredTimeZone: string = null;

  isAbstract = false;
  isAffiliation = false;
  isCountry = false;
  isEmail = false;
  loadedSessions= false;

  public days: Array<any>;

  public timeZoneForm: FormGroup;
  public listTimeZones: Array<any>;
  public cloneEvent: Event;

  constructor(
    private adminService: AdminService,
    private http: HttpClient,
    private eventService: EventsService,
    private submissionsService: SubmissionsService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    public fb: FormBuilder,
    private timezoneService: TimezoneService,
    private countryPipe: CountryPipe,
    private translate: TranslateService,
    private elementRef: ElementRef,
    private dialog: MatDialog,
    private zone: NgZone
  ) { }

  @ViewChild('toolbar') toolbar: ElementRef<HTMLElement>;
  @ViewChild('toolbarSpacer') toolbarSpacer: ElementRef<HTMLElement>;

  /** Loop de sincronia da geometria da barra fixa (ver `trackFixedToolbar`). */
  private toolbarRafId = 0;
  private lastLeft = -1;
  private lastWidth = -1;
  private lastHeight = -1;
  /** Página rolada? Liga a sombra sob a barra (classe `is-scrolled` no host). */
  private lastScrolled = false;
  /** Área de conteúdo, âncora da geometria (em cache). */
  private canvasEl: HTMLElement | null = null;

  /**
   * Mantém a barra de controles presa à VIEWPORT (`position: fixed`) com a
   * geometria horizontal copiada da coluna de conteúdo — a mesma técnica do
   * cabeçalho da Lista de Submissões e da Visão de Calendário.
   *
   * Por que `fixed` e não `sticky`: sticky ancora no DOCUMENTO, e o rubber-band
   * do overscroll move o documento inteiro nos extremos — a barra "treme" no
   * início e no fim da página. `fixed` ancora na viewport, que não se move.
   *
   * Por que um loop por frame: abrir/fechar o menu lateral muda a POSIÇÃO do
   * conteúdo sem mudar o tamanho do observado (ResizeObserver não dispara) e
   * nenhum evento cobre os quadros da animação.
   */
  private readonly trackFixedToolbar = (): void => {
    this.syncToolbarGeometry();
    this.toolbarRafId = requestAnimationFrame(this.trackFixedToolbar);
  };

  /** Um passo de sincronia. Separado do loop para poder rodar já no
   *  `ngAfterViewInit`, antes da primeira pintura. */
  private syncToolbarGeometry(): void {
    const bar = this.toolbar?.nativeElement;
    const spacer = this.toolbarSpacer?.nativeElement;

    if (bar && spacer) {
      // Âncora = a ÁREA DE CONTEÚDO INTEIRA (`mat-sidenav-content`), não o host:
      // o host vive recuado pelo padding lateral do shell, e medi-lo deixaria
      // uma faixa descoberta entre o menu lateral e a barra. Mesma escolha do
      // header do "Modelo de certificado".
      const host = this.elementRef.nativeElement as HTMLElement;
      if (!this.canvasEl) {
        this.canvasEl = (host.closest('mat-sidenav-content') as HTMLElement) || host;
      }
      const rect = this.canvasEl.getBoundingClientRect();

      if (rect.left !== this.lastLeft || rect.width !== this.lastWidth) {
        bar.style.left = `${rect.left}px`;
        bar.style.width = `${rect.width}px`;
        this.lastLeft = rect.left;
        this.lastWidth = rect.width;
      }

      // A barra sai do fluxo: o espaçador devolve a altura dela.
      const height = bar.offsetHeight;
      if (height !== this.lastHeight) {
        spacer.style.height = `${height}px`;
        this.lastHeight = height;
      }
    }

    // Sombra sob a barra só quando há folha passando por baixo. Enquanto um
    // diálogo está aberto o CDK trava a rolagem (`cdk-global-scrollblock` põe
    // `position: fixed` no <html>) e `pageYOffset` vale 0 — congelamos o estado
    // nesse caso, senão a sombra sumiria ao abrir o diálogo.
    if (!document.documentElement.classList.contains('cdk-global-scrollblock')) {
      const scrolled = window.pageYOffset > 0;
      if (scrolled !== this.lastScrolled) {
        this.elementRef.nativeElement.classList.toggle('is-scrolled', scrolled);
        this.lastScrolled = scrolled;
      }
    }

  }

  ngAfterViewInit(): void {
    // Fora da zona: um rAF por frame dentro dela dispararia detecção de
    // mudanças a 60fps na página inteira.
    this.zone.runOutsideAngular(() => {
      // Primeira medição SÍNCRONA: `ngAfterViewInit` roda antes da pintura,
      // então a barra já nasce na posição certa. O loop cobre o que muda
      // depois (abrir/fechar o menu lateral, redimensionar a janela).
      this.syncToolbarGeometry();
      this.toolbarRafId = requestAnimationFrame(this.trackFixedToolbar);
    });
  }

  ngOnDestroy(): void {
    if (this.toolbarRafId) { cancelAnimationFrame(this.toolbarRafId); }
    if (this.sheetStyleEl) { this.sheetStyleEl.remove(); }
  }

  /**
   * Aplica a folha do programa na página. É a MESMA CSS embutida no arquivo
   * exportado — por isso vive num `<style>` injetado, e não no SCSS do
   * componente: garante que o que se vê seja o que se baixa.
   */
  private applyProgramSheet(): void {
    if (!this.sheetStyleEl) {
      this.sheetStyleEl = document.createElement('style');
      this.sheetStyleEl.setAttribute('data-program-sheet', '');
      document.head.appendChild(this.sheetStyleEl);
    }
    this.sheetStyleEl.textContent = this.programCss();
  }

  /** Folha completa: base + bloco (gerado) com a cor de cada sessão. */
  private programCss(): string {
    const comments = this.sheetComments();
    const colorRules = this.sessionColorRules();
    // Sem nenhuma sessão de cor própria, o bloco inteiro (título incluso) some.
    return colorRules
      ? `${programSheetCss(comments)}\n${programSheetColorsHeader(comments)}\n${colorRules}`
      : programSheetCss(comments);
  }

  /**
   * Comentários da folha no idioma da interface: quem baixa o arquivo para
   * editar pode não falar português.
   */
  private sheetComments(): ProgramSheetComments {
    const t = (key: string) => this.translate.instant(`admin.event.sessions.program.sheet.${key}`);
    return {
      intro: t('intro'),
      colors: t('colors'),
      typography: t('typography'),
      reset: t('reset'),
      session: t('session'),
      sessionText: t('sessionText'),
      meta: t('meta'),
      papers: t('papers'),
      links: t('links'),
      paper: t('paper'),
      lightOnly: t('lightOnly'),
      sessionColors: t('sessionColors')
    };
  }

  /**
   * Uma regra por sessão com a cor de fundo E a cor de texto que contrasta com
   * ela (calculada por luminância, `lib/color-contrast`). Fica em CSS — e não
   * em `style` no elemento — para o usuário trocar a paleta inteira editando a
   * folha do arquivo baixado; as duas cores de uma sessão ficam lado a lado.
   */
  /**
   * Uma sessão tem cor PRÓPRIA quando o organizador escolheu algo diferente do
   * padrão. Só essas ganham regra no fim da folha; as demais seguem
   * `--session-color`/`--session-text` do topo — que é o que permite a quem
   * edita o arquivo trocar a cor de todas de uma vez.
   */
  private hasCustomColor(session: any): boolean {
    const color = session && session.backgroundColor;
    return !!color && color.toLowerCase() !== DEFAULT_SESSION_COLOR.toLowerCase();
  }

  /**
   * Chave da cor, usada como classe. Cores da paleta canônica entram pelo NOME
   * (`session-color-lilac`), que se lê no HTML; as de fora, pelo hexadecimal
   * (`session-color-7b2d8e`), que ao menos identifica.
   */
  private colorKey(color: string): string {
    const name = paletteNameOf(color);
    return `session-color-${name || color.replace('#', '').toLowerCase()}`;
  }

  /**
   * Uma regra por COR, não por sessão: sessões que usam a mesma cor da paleta
   * compartilham a mesma linha, então quem edita o arquivo muda uma cor e todas
   * as sessões daquela cor acompanham. A cor padrão não entra aqui — quem a usa
   * segue as variáveis do topo da folha.
   */
  private sessionColorRules(): string {
    const sessions = (this.cloneEvent && this.cloneEvent.sessions) || [];
    const byColor = new Map<string, { color: string, titles: string[] }>();

    sessions.filter(session => this.hasCustomColor(session)).forEach(session => {
      const key = this.colorKey(session.backgroundColor);
      const group = byColor.get(key) || { color: session.backgroundColor, titles: [] };
      group.titles.push(session.title);
      byColor.set(key, group);
    });

    return Array.from(byColor.entries())
      .map(([key, group]) => {
        const text = isLightBackground(group.color) ? '#373734' : '#ffffff';
        // Os títulos viram comentário: sem eles, quem edita vê só uma cor e não
        // sabe o que vai mudar.
        return `/* ${group.titles.join(', ')} */\n.program .${key} {\n` +
               `  --session-color: ${group.color};\n` +
               `  --session-text: ${text};\n}`;
      })
      .join('\n\n');
  }

  /** Classe que liga a sessão à sua regra de cor. */
  sessionColorClass(session: any): string {
    // Sem cor própria, sem classe: o HTML exportado fica mais limpo e a sessão
    // herda a cor padrão da folha.
    return this.hasCustomColor(session) ? this.colorKey(session.backgroundColor) : '';
  }

  /**
   * Sufixo "(SIGLA, País)" de um autor/coordenador, conforme os interruptores
   * de afiliação e país. Montado no TS para o template não repetir a mesma
   * cadeia de `ng-container` em dois lugares.
   */
  personSuffix(user: any): string {
    const acronym = this.isAffiliation ? user?.profile?.affiliation?.acronym : null;
    const country = this.isCountry && user?.profile?.country
      ? this.countryPipe.transform(user.profile.country)
      : null;
    const parts = [acronym, country].filter(part => !!part);
    return parts.length ? ` (${parts.join(', ')})` : '';
  }

  /**
   * Lê os toggles gravados. São preferências de VISUALIZAÇÃO, iguais para
   * qualquer evento — por isso uma chave só, sem id de evento.
   */
  private restoreFlags(): void {
    try {
      const raw = localStorage.getItem(EventProgramComponent.FLAGS_STORAGE_KEY);
      if (!raw) { return; }
      const flags = JSON.parse(raw);
      this.isAbstract = !!flags.abstract;
      this.isAffiliation = !!flags.affiliation;
      this.isCountry = !!flags.country;
      this.isEmail = !!flags.email;
      this.restoredTimeZone = flags.timeZone || null;
    } catch { /* JSON corrompido ou storage indisponível — segue no default */ }
  }

  /** Chamado a cada troca de toggle. */
  persistFlags(): void {
    try {
      localStorage.setItem(EventProgramComponent.FLAGS_STORAGE_KEY, JSON.stringify({
        abstract: this.isAbstract,
        affiliation: this.isAffiliation,
        country: this.isCountry,
        email: this.isEmail,
        timeZone: this.timeZoneForm ? this.timeZoneForm.get('programTimeZone').value : null
      }));
    } catch { /* quota/modo privado — silencioso */ }
  }

  ngOnInit(): void {
    this.restoreFlags();
    this.initFormTimeZone();
    this.applyProgramSheet();
    setTimeout(() => {
      this.adminService.progressBar.start();
      this.route.params.subscribe(params => {
        this.event = this.adminService.selectedEvent;
        this.adminService.getEvent().subscribe(event => {
          this.event = event;
          this.applyEventTimeZone(this.event);
          this.eventService.getSessionsByEvent(event.id).subscribe(sessions => {
            this.event.sessions = sessions;
            this.loadedSessions = true;
          }, () => {},
          () => {
            this.event.sessions = this.event.sessions.sort((a, b) => {
              if(<any>a.start > <any>b.start){
                return 1;
              }else if(<any>a.start === <any>b.start){
                if(<any>a.backgroundColor > <any>b.backgroundColor){
                  return 1;
                }else{
                  return -1;
                }
              }else{
                return -1;
              }
            });
            // Fuso ESCOLHIDO (pode vir lembrado de outra visita), não o do
            // evento — senão a folha abriria sempre no fuso do evento e só
            // respeitaria a preferência depois de uma troca manual.
            this.convertScheduleToTimeZone(this.event, this.offsetSelectedTimezone);
            this.adminService.progressBar.stop();
          });
        });
      });
    });
  }

  /**
   * Monta o seletor de fuso ANTES de qualquer ida ao servidor: o fuso do
   * navegador é conhecido de imediato, então o campo nasce junto com o resto da
   * barra. Sem isto ele dependia da resposta do `getEvent()` e aparecia depois
   * dos toggles — e às vezes depois da própria folha.
   */
  /** Rótulos das opções "do evento"/"do navegador", no idioma da interface. */
  private timeZoneLabels(): calendarTimezone.TimeZoneLabels {
    return {
      event: this.translate.instant('forms.fields.legend.event-time-zone'),
      browser: this.translate.instant('forms.fields.legend.browser-time-zone')
    };
  }

  private initFormTimeZone(): void {
    this.listTimeZones = calendarTimezone.toSelectable(null, moment.tz.guess(), this.timeZoneLabels());
    this.timeZoneForm = this.fb.group({
      // `event` só passa a existir quando o evento chega (ver
      // `applyEventTimeZone`); até lá a escolha restaurada equivalente é o
      // fuso do navegador.
      programTimeZone: [this.restoredTimeZone === 'event' ? 'browser' : (this.restoredTimeZone || 'browser')]
    });
  }

  /**
   * Chegou o evento: a lista ganha a opção do fuso DO EVENTO e ela passa a ser
   * a escolha corrente. Atualiza em vez de recriar o formulário — recriar
   * descartaria uma troca de fuso feita nesse meio tempo.
   */
  private applyEventTimeZone(event: Event): void {
    const eventTimeZone: string = event && event.eventInfo ? event.eventInfo.timeZone : null;
    this.listTimeZones = calendarTimezone.toSelectable(eventTimeZone, moment.tz.guess(), this.timeZoneLabels());

    // A escolha lembrada vence o default, desde que ainda exista na lista.
    const remembered = this.restoredTimeZone &&
      this.listTimeZones.find(timeZone => timeZone.id === this.restoredTimeZone);
    const control = this.timeZoneForm.get('programTimeZone');
    if (remembered) {
      control.setValue(this.restoredTimeZone, { emitEvent: false });
    } else if (eventTimeZone) {
      control.setValue('event', { emitEvent: false });
    }
  }

  /** Offset do fuso atualmente escolhido (o da lista, não necessariamente o do evento). */
  private get offsetSelectedTimezone(): number {
    const id = this.timeZoneForm.get('programTimeZone').value;
    const selected = this.listTimeZones.find(timeZone => timeZone.id === id);
    return selected ? selected.offset : this.offsetEventTimezone;
  }

  public changeTimeZone($id: string): void {
    const offsetTimeZoneSelected = this.listTimeZones.find(timeZone => timeZone.id === $id).offset;
    this.convertScheduleToTimeZone(this.event, offsetTimeZoneSelected);
    this.restoredTimeZone = $id;
    this.persistFlags();
  }

  private convertScheduleToTimeZone(event: Event, offsetTimeZone: number): void {
    this.cloneEvent = _.cloneDeep(event);
    this.cloneEvent.sessions.forEach(session => {
      session.start = Dayjs(this.timezoneService.timeZoneConverter(session.start.toDate(), offsetTimeZone));
      session.end = Dayjs(this.timezoneService.timeZoneConverter(session.end.toDate(), offsetTimeZone));
    });
    // As regras de cor são geradas a partir das sessões — refaz a folha.
    this.applyProgramSheet();
  }

  public get offsetEventTimezone(): number {
    const nameTimeZone: string = this.event.eventInfo.timeZone;
    const offsetTimeZone: number = moment.tz(nameTimeZone).utcOffset();

    return offsetTimeZone;
  }

  // private reorder(): void {
  //   this.event.sessions = this.event.sessions.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  // }

  submitStatusChange(element) {
    this.submissionsService.editSubmission({ noShow: true }, element.id).subscribe(() => {
        this.notificationService.notify('submissions.edit.status', { params: { id: element.id } });
    });
  }

  /**
   * Baixa a folha como HTML autocontido: o mesmo markup que está na tela e a
   * MESMA folha de estilo, embutida em `<style>`.
   *
   * Antes o arquivo levava `<style>`/`<link>` do app inteiro (via
   * `ExportService`) e quase nenhuma regra própria — o oposto do objetivo, que
   * é um arquivo simples de customizar editando poucas regras.
   */
  /**
   * Atributos de um elemento, prontos para o arquivo exportado.
   *
   * Tira o que é do Angular em DOIS níveis: os atributos (`_ngcontent-*`,
   * `ng-reflect-*`) e as classes que ele injeta no `class`
   * (`ng-star-inserted`, `ng-tns-*`, `ng-trigger-*`) — estas passavam pelo
   * filtro de atributos porque o atributo se chama `class`. Se não sobrar
   * classe alguma, o atributo inteiro sai.
   */
  private exportAttributes(element: Element): string {
    return Array.from(element.attributes)
      .filter(attr => attr.name.indexOf('_ng') !== 0 && attr.name.indexOf('ng-') !== 0)
      .map(attr => {
        if (attr.name !== 'class') { return ` ${attr.name}="${attr.value}"`; }
        const classes = attr.value.split(/\s+/)
          .filter(name => !!name && name.indexOf('ng-') !== 0 && name.indexOf('_ng') !== 0);
        return classes.length ? ` class="${classes.join(' ')}"` : '';
      })
      .join('');
  }

  /**
   * Editar sessão (botão no canto inferior direito do card, visível no hover).
   * Reusa o MESMO diálogo da Visão de Calendário — mesma geometria e mesmo
   * contrato de `saveSession`, para as duas telas não divergirem.
   */
  editSession(session: any, startDirty = false): void {
    this.dialog.open(GeneralSessionDialogComponent, {
      width: '625px',
      height: '450px',
      maxHeight: '85vh',
      panelClass: ['my-custom-dialog-class', 'session-dialog-panel'],
      disableClose: true,
      autoFocus: false,
      data: {
        params: {
          session,
          eventTracks: this.event.tracks,
          eventTimeZone: this.event.eventInfo.timeZone,
          startDirty,
          saveSession: (fromDialog) => this.saveSession(fromDialog)
        }
      }
    });
  }

  /**
   * Card fantasma ao fim da lista: abre o MESMO diálogo, com uma sessão nova.
   * Começa no dia seguinte à última sessão do programa (ou hoje, se não houver
   * nenhuma), com 30 minutos de duração — mesma duração default da criação pelo
   * calendário. Cabe ao organizador ajustar no diálogo.
   */
  addSession(): void {
    const start = this.nextSessionStart();
    const session: any = {
      id: null,
      event: this.event.id,
      // Título no idioma da interface — o calendário cria com "New session"
      // fixo, o que aparecia em português também.
      title: this.translate.instant('admin.event.sessions.new-session-title'),
      subtitle: '',
      url: '',
      room: '',
      start,
      end: moment(start).add(30, 'minutes').toDate(),
      chair: null,
      submissions: [],
      backgroundColor: DEFAULT_SESSION_COLOR
    };
    // Sessão que ainda não existe no servidor: o diálogo abre sujo, então
    // "Salvar" já está disponível e fechar avisa que a criação se perde.
    this.editSession(session, true);
  }

  /**
   * Início sugerido para uma sessão nova: logo após o FIM da última sessão do
   * programa. Sem sessões, às 8h do primeiro dia do evento — e, se o evento não
   * tiver data de início, às 8h de hoje, para o diálogo nunca abrir com data
   * inválida.
   */
  private nextSessionStart(): Date {
    const sessions = (this.cloneEvent && this.cloneEvent.sessions) || [];
    if (sessions.length) {
      const lastEnd = sessions
        .map(session => moment(session.end.toDate()))
        .reduce((latest, current) => (current.isAfter(latest) ? current : latest));
      return lastEnd.toDate();
    }

    const beginAt = this.event && this.event.eventInfo && this.event.eventInfo.beginAt;
    const firstDay = beginAt ? moment(beginAt.toDate()) : moment();
    return firstDay.startOf('day').add(8, 'hours').toDate();
  }

  /** Grava a sessão editada e recarrega a folha. */
  private saveSession(fromDialog: any): void {
    const payload = { ...fromDialog, chair: fromDialog.chair?.id ?? null };
    const done = () => {
      this.dialog.closeAll();
      this.reloadSessions();
    };
    const failed = () => this.notificationService.notifyError('admin.event.sessions.program.save-error');

    // Sem `id` é sessão NOVA (veio do card fantasma).
    if (payload.id) {
      this.eventService.editSession(payload, payload.id).subscribe(done, failed);
    } else {
      this.eventService.createSession(payload).subscribe(done, failed);
    }
  }

  /** Excluir sessão (x no canto superior direito), com confirmação. */
  removeSession(session: any): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.sessions.delete-dialog.title',
        content: 'admin.event.sessions.delete-dialog.content',
        params: { content: { title: session.title } },
        // Chave própria: a global `forms.buttons.delete` é "Deletar".
        confirm: 'admin.event.sessions.delete-dialog.confirm',
        destructive: true
      }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      this.eventService.deleteSession(session.id, this.event).subscribe(() => this.reloadSessions());
    });
  }

  /**
   * Recarrega as sessões e refaz a folha (inclusive as cores, que dependem das
   * sessões). Mesma cadeia usada na carga inicial.
   */
  private reloadSessions(): void {
    this.eventService.getSessionsByEvent(this.event.id).subscribe(sessions => {
      this.event.sessions = sessions;
      this.convertScheduleToTimeZone(this.event, this.offsetSelectedTimezone);
      this.applyProgramSheet();
    });
  }

  /** Recua cada linha de um texto por `level` níveis de 2 espaços. */
  private indent(text: string, level: number): string {
    const pad = '  '.repeat(level);
    return text.split('\n').map(line => (line.trim() ? pad + line : '')).join('\n');
  }

  /**
   * Serializa a folha para o arquivo baixado.
   *
   * Não dá para usar `outerHTML`: ele traz os atributos do Angular
   * (`_ngcontent-*`, `ng-reflect-*`), os comentários de âncora do `*ngIf`/`*ngFor`
   * e a indentação do template — que aqui nasce com muitos níveis por causa da
   * estrutura da página. Quem abre o arquivo para editar à mão precisa de um
   * HTML que se leia: uma tag por linha, dois espaços por nível, sem ruído.
   */
  private serializeForExport(element: Element, level: number): string {
    const pad = '  '.repeat(level);
    const VOID_TAGS = ['br', 'hr', 'img', 'meta', 'link', 'input'];

    const tag = element.tagName.toLowerCase();
    const attrs = this.exportAttributes(element);

    if (VOID_TAGS.indexOf(tag) >= 0) {
      return `${pad}<${tag}${attrs}>`;
    }

    // Filhos que interessam: elementos e texto com conteúdo. Comentários (as
    // âncoras do Angular) ficam de fora.
    const children = Array.from(element.childNodes).filter(node => {
      // `data-editor` marca a camada de EDIÇÃO (botões de excluir/editar que
      // aparecem no hover): ela é da tela, não do documento — o arquivo baixado
      // não a inclui.
      if (node.nodeType === Node.ELEMENT_NODE &&
          (node as Element).hasAttribute('data-editor')) { return false; }
      return node.nodeType === Node.ELEMENT_NODE ||
        (node.nodeType === Node.TEXT_NODE && (node.textContent || '').trim().length > 0);
    });

    if (children.length === 0) {
      return `${pad}<${tag}${attrs}></${tag}>`;
    }

    // Conteúdo puramente textual (ou com marcação inline, como um link no meio
    // da frase) fica na MESMA linha: quebrar aqui inseriria espaços onde o
    // texto não os tem.
    const isInlineContent = children.every(node =>
      node.nodeType === Node.TEXT_NODE ||
      ['a', 'span', 'em', 'strong', 'b', 'i'].indexOf((node as Element).tagName.toLowerCase()) >= 0);

    if (isInlineContent) {
      const inline = children.map(node => node.nodeType === Node.TEXT_NODE
        ? (node.textContent || '').replace(/\s+/g, ' ').trim()
        : this.serializeInline(node as Element)).join(' ').replace(/ ([,.;:])/g, '$1');
      return `${pad}<${tag}${attrs}>${inline}</${tag}>`;
    }

    const inner = children
      .map(node => node.nodeType === Node.TEXT_NODE
        ? '  '.repeat(level + 1) + (node.textContent || '').replace(/\s+/g, ' ').trim()
        : this.serializeForExport(node as Element, level + 1))
      .join('\n');
    return `${pad}<${tag}${attrs}>\n${inner}\n${pad}</${tag}>`;
  }

  /** Versão de uma linha, para marcação que vive dentro de um parágrafo. */
  private serializeInline(element: Element): string {
    const tag = element.tagName.toLowerCase();
    const attrs = this.exportAttributes(element);
    const inner = Array.from(element.childNodes)
      .filter(node => node.nodeType !== Node.COMMENT_NODE)
      .map(node => node.nodeType === Node.TEXT_NODE
        ? (node.textContent || '').replace(/\s+/g, ' ')
        : this.serializeInline(node as Element))
      .join('');
    return `<${tag}${attrs}>${inner}</${tag}>`;
  }

  generateExportHtml(): void {
    const sheet = document.getElementById('program');
    if (!sheet) { return; }

    const html = `<!doctype html>
<html lang="${this.documentLang}">
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>${this.event.name}</title>
    <style>
${this.indent(this.programCss().trim(), 3)}
    </style>
  </head>
  <body>
${this.serializeForExport(sheet, 2)}
  </body>
</html>
`;

    const blob = new Blob([html], { type: 'text/html' });
    const downloadURL = URL.createObjectURL(blob);
    this.downloadLink(downloadURL, `${this.event.name}.html`);
    URL.revokeObjectURL(downloadURL);
  }

  private get documentLang(): string {
    return document.documentElement.lang || 'pt-BR';
  }

  downloadLink(downloadURL: string, name: string) {
    // Create link to lownload
    var link = document.createElement('a');
    link.href = downloadURL;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = name;
    link.click();
  }

  printToPDF() {

  }
}
